import { Action, Reducer } from 'redux';

import {
    ReservationDTO,
    ReservationListFilter,
    TableAvailabilityDTO,
} from 'api/controllers/reservations';

import { matchesReservationFilter } from 'helpers/reservationHelper';
import { ReservationsAction } from './actions';

export interface ReservationsState {
    requests: ReservationDTO[];
    requestsRestaurantId: string | null;
    selectedRequestId: string | null;
    /** Open requests matching the REQUESTS tab filter; filter null means the full `requests` list is shown. */
    requestsView: {
        filter: ReservationListFilter | null;
        items: ReservationDTO[];
        isLoading: boolean;
    };
    /** day and search describe the loaded reservations; day null means all dates. */
    active: {
        day: string | null | undefined;
        search: string;
        reservations: ReservationDTO[];
        isLoading: boolean;
    };
    selectedActiveId: string | null;
    availability: {
        key: string | null;
        tables: TableAvailabilityDTO[];
    };
}

export const initialState: ReservationsState = {
    requests: [],
    requestsRestaurantId: null,
    selectedRequestId: null,
    requestsView: { filter: null, items: [], isLoading: false },
    active: { day: undefined, search: '', reservations: [], isLoading: false },
    selectedActiveId: null,
    availability: { key: null, tables: [] },
};

function isOpenRequest(reservation: ReservationDTO): boolean {
    return reservation.status === 'Requested' && !reservation.isArchived;
}

function matchesActive(state: ReservationsState, reservation: ReservationDTO): boolean {
    const { day, search } = state.active;
    return day !== undefined && matchesReservationFilter(reservation, { day, search });
}

function applyToRequestsView(
    view: ReservationsState['requestsView'],
    reservation: ReservationDTO
): ReservationsState['requestsView'] {
    if (!view.filter) {
        return view;
    }
    const items = view.items.filter((item) => item.id !== reservation.id);
    if (isOpenRequest(reservation) && matchesReservationFilter(reservation, view.filter)) {
        const index = view.items.findIndex((item) => item.id === reservation.id);
        items.splice(index < 0 ? items.length : index, 0, reservation);
    }
    return { ...view, items };
}

function withoutRequest(state: ReservationsState, id: string): ReservationsState {
    return {
        ...state,
        requests: state.requests.filter((request) => request.id !== id),
        requestsView: {
            ...state.requestsView,
            items: state.requestsView.items.filter((request) => request.id !== id),
        },
        selectedRequestId: state.selectedRequestId === id ? null : state.selectedRequestId,
    };
}

export const reducer: Reducer<ReservationsState> = (
    state: ReservationsState | undefined,
    incomingAction: Action
): ReservationsState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as ReservationsAction;

    switch (action.type) {
        case 'RESERVATION_REQUESTS_SET': {
            const { filter } = state.requestsView;
            const isSameRestaurant =
                state.requestsRestaurantId === null ||
                state.requestsRestaurantId === action.restaurantId;
            return {
                ...state,
                requests: action.requests,
                requestsRestaurantId: action.restaurantId,
                requestsView:
                    filter && isSameRestaurant
                        ? {
                              ...state.requestsView,
                              items: action.requests.filter(
                                  (request) =>
                                      isOpenRequest(request) &&
                                      matchesReservationFilter(request, filter)
                              ),
                          }
                        : isSameRestaurant
                        ? state.requestsView
                        : initialState.requestsView,
                selectedRequestId: action.requests.some(
                    (request) => request.id === state.selectedRequestId
                )
                    ? state.selectedRequestId
                    : null,
            };
        }
        case 'RESERVATION_REQUESTS_VIEW_LOAD':
            return { ...state, requestsView: { ...state.requestsView, isLoading: true } };
        case 'RESERVATION_REQUESTS_VIEW_SET':
            return {
                ...state,
                requestsView: { filter: action.filter, items: action.requests, isLoading: false },
            };
        case 'RESERVATION_REQUESTS_VIEW_FAIL':
            return { ...state, requestsView: { ...state.requestsView, isLoading: false } };
        case 'RESERVATION_REQUEST_SELECT':
            return { ...state, selectedRequestId: action.id };
        case 'RESERVATION_REQUEST_SAVED':
            return {
                ...state,
                requests: state.requests.map((request) =>
                    request.id === action.reservation.id ? action.reservation : request
                ),
                requestsView: applyToRequestsView(state.requestsView, action.reservation),
            };
        case 'RESERVATION_REQUEST_REMOVED':
            return withoutRequest(state, action.id);
        case 'RESERVATION_CONFIRMED': {
            const { reservation } = action;
            const next = withoutRequest(state, reservation.id);
            return {
                ...next,
                selectedActiveId: reservation.id,
                active: matchesActive(next, reservation)
                    ? {
                          ...next.active,
                          reservations: next.active.reservations
                              .filter((item) => item.id !== reservation.id)
                              .concat(reservation),
                      }
                    : next.active,
            };
        }
        case 'RESERVATION_REALTIME_UPDATE': {
            const { reservation } = action;
            let next = state;
            if (state.requestsRestaurantId === reservation.restaurantId) {
                if (isOpenRequest(reservation)) {
                    const exists = state.requests.some((item) => item.id === reservation.id);
                    next = {
                        ...next,
                        requests: exists
                            ? state.requests.map((item) =>
                                  item.id === reservation.id ? reservation : item
                              )
                            : state.requests.concat(reservation),
                        requestsView: applyToRequestsView(state.requestsView, reservation),
                    };
                } else {
                    next = withoutRequest(state, reservation.id);
                }
            }

            const isActive =
                !reservation.isArchived &&
                (reservation.status === 'Confirmed' || reservation.status === 'Seated');
            const matchesLoaded = matchesActive(next, reservation);
            const isLoaded = next.active.reservations.some((item) => item.id === reservation.id);
            if (isLoaded || (isActive && matchesLoaded)) {
                const reservations = next.active.reservations.filter(
                    (item) => item.id !== reservation.id
                );
                if (isActive && matchesLoaded) {
                    reservations.push(reservation);
                }
                next = {
                    ...next,
                    active: { ...next.active, reservations },
                    selectedActiveId:
                        next.selectedActiveId === reservation.id &&
                        !reservations.some((item) => item.id === reservation.id)
                            ? null
                            : next.selectedActiveId,
                };
            }
            return next;
        }
        case 'RESERVATION_ACTIVE_LOAD': {
            const isSameList =
                action.day === state.active.day && action.search === state.active.search;
            return {
                ...state,
                active:
                    isSameList || action.keepPrevious
                        ? { ...state.active, isLoading: true }
                        : { day: action.day, search: action.search, reservations: [], isLoading: true },
            };
        }
        case 'RESERVATION_ACTIVE_SET':
            return {
                ...state,
                active: {
                    day: action.day,
                    search: action.search,
                    reservations: action.reservations,
                    isLoading: false,
                },
            };
        case 'RESERVATION_ACTIVE_FAIL':
            return { ...state, active: { ...state.active, isLoading: false } };
        case 'RESERVATION_ACTIVE_SELECT':
            return { ...state, selectedActiveId: action.id };
        case 'RESERVATION_AVAILABILITY_LOAD':
            return action.key === state.availability.key
                ? state
                : { ...state, availability: { key: action.key, tables: [] } };
        case 'RESERVATION_AVAILABILITY_SET':
            return action.key === state.availability.key
                ? { ...state, availability: { key: action.key, tables: action.tables } }
                : state;
        case 'RESERVATION_AVAILABILITY_CLEAR':
            return { ...state, availability: initialState.availability };
        default:
            return state;
    }
};
